import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, TrendingDown, Package, FolderOpen } from 'lucide-react';
import { categoriesApi } from '../../api/endpoints/categories';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { CurrencyDisplay } from '../../components/ui/CurrencyDisplay';
import { getCdnUrl } from '../../lib/formatters';
import { useStore } from '../../contexts/StoreContext';

const MONTH_NAMES_AR = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

interface CategoryStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: string;
  categoryName?: string;
}

export function CategoryStatsModal({ isOpen, onClose, categoryId, categoryName }: CategoryStatsModalProps) {
  const { storeId } = useParams<{ storeId: string }>();
  const { t, i18n } = useTranslation();
  const { currentStore } = useStore();
  const isAr = i18n.language === 'ar';

  const { data, isLoading } = useQuery({
    queryKey: ['category-stats', storeId, categoryId],
    queryFn: () => categoriesApi.stats(storeId!, categoryId),
    enabled: !!storeId && !!categoryId && isOpen,
  });

  const chartData = (data?.chart || []).map((d: any) => {
    const [y, m] = d.month.split('-');
    const label = isAr ? MONTH_NAMES_AR[parseInt(m) - 1] : d.month;
    return { ...d, label };
  });

  const growthRate = data?.lastMonth?.sales > 0
    ? (((data.thisMonth.sales - data.lastMonth.sales) / data.lastMonth.sales) * 100).toFixed(1)
    : data?.thisMonth?.sales > 0 ? '100' : '0';
  const isGrowthPositive = Number(growthRate) >= 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={categoryName || t('categories.stats', 'Category Stats')} size="lg">
      {isLoading ? (
        <div className="flex justify-center py-16"><LoadingSpinner /></div>
      ) : !data ? (
        <p className="text-center text-text-muted py-8">{t('common.error')}</p>
      ) : (
        <div className="space-y-6">
          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl border border-border-default bg-bg-tertiary p-4 text-center">
              <p className="text-xs text-text-muted mb-1">{t('categories.productCount', 'Products')}</p>
              <p className="text-2xl font-bold">{data.productCount}</p>
            </div>
            <div className="rounded-xl border border-border-default bg-bg-tertiary p-4 text-center">
              <p className="text-xs text-text-muted mb-1">{t('products.totalRevenue', 'Total Revenue')}</p>
              <p className="text-2xl font-bold"><CurrencyDisplay amount={data.totalRevenue} /></p>
            </div>
            <div className="rounded-xl border border-border-default bg-bg-tertiary p-4 text-center">
              <p className="text-xs text-text-muted mb-1">{t('products.thisMonth', 'This Month')}</p>
              <p className="text-2xl font-bold">{data.thisMonth.sales}</p>
              <p className="text-xs text-text-muted"><CurrencyDisplay amount={data.thisMonth.revenue} /></p>
            </div>
            <div className="rounded-xl border border-border-default bg-bg-tertiary p-4 text-center">
              <p className="text-xs text-text-muted mb-1">{t('products.growth', 'Growth')}</p>
              <div className={`flex items-center justify-center gap-1 text-xl font-bold ${isGrowthPositive ? 'text-success' : 'text-danger'}`}>
                {isGrowthPositive ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
                {growthRate}%
              </div>
              <p className="text-xs text-text-muted">{t('products.vsLastMonth', 'vs last month')}</p>
            </div>
          </div>

          {/* Chart */}
          {chartData.length > 0 && (
            <div className="rounded-xl border border-border-default bg-bg-tertiary p-4">
              <h3 className="text-sm font-semibold mb-3">{t('products.salesOverTime', 'Sales Over Time')}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-default)" />
                  <XAxis dataKey="label" tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }} />
                  <YAxis tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-default)', borderRadius: 8 }}
                    labelStyle={{ color: 'var(--color-text-primary)' }}
                    formatter={(value: number, name: string) => [
                      name === 'revenue' ? `${value.toLocaleString()} SAR` : value,
                      name === 'revenue' ? t('products.revenue', 'Revenue') : t('products.sales', 'Sales')
                    ]}
                  />
                  <Area type="monotone" dataKey="sales" stroke="#2196F3" fill="#2196F3" fillOpacity={0.15} strokeWidth={2} />
                  <Area type="monotone" dataKey="revenue" stroke="#4CAF50" fill="#4CAF50" fillOpacity={0.1} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Top products */}
          {data.topProducts?.length > 0 && (
            <div className="rounded-xl border border-border-default bg-bg-tertiary p-4">
              <h3 className="text-sm font-semibold mb-3">{t('categories.topProducts', 'Top Products')}</h3>
              <div className="space-y-2">
                {data.topProducts.map((p: any, i: number) => (
                  <div key={p._id} className="flex items-center justify-between gap-3 rounded-lg bg-bg-secondary px-3 py-2.5">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xs text-text-muted font-bold w-5 text-center shrink-0">{i + 1}</span>
                      {p.previewImage ? (
                        <img src={getCdnUrl(p.previewImage, currentStore?._id)} alt="" className="h-9 w-9 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-bg-tertiary shrink-0">
                          <Package className="h-4 w-4 text-text-muted" />
                        </div>
                      )}
                      <p className="text-sm font-medium truncate">{p.title}</p>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <span className="text-xs text-text-muted">{p.sales} {t('products.sold', 'sold')}</span>
                      <span className="text-sm font-semibold"><CurrencyDisplay amount={p.revenue} /></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {data.totalSales === 0 && (
            <div className="text-center py-8">
              <FolderOpen className="h-12 w-12 text-text-muted/30 mx-auto mb-3" />
              <p className="text-text-muted">{t('categories.noSalesYet', 'No sales yet in this category')}</p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
